import React, { useState, useEffect, useCallback, useRef } from 'react';
import { File, Image, FileText, FileSpreadsheet, Trash2, Loader2, Pencil, Check, X, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../services/api';
import { formatDateTime as formatDateTimeUtil, getBrowserTimezone } from '../../utils/dateUtils';

// File type icons
const getFileIcon = (mimeType, filename) => {
  if (mimeType?.startsWith('image/')) return Image;
  if (mimeType?.includes('spreadsheet') || filename?.match(/\.(xlsx?|csv)$/i)) return FileSpreadsheet;
  if (mimeType?.includes('pdf') || mimeType?.includes('document') || mimeType?.includes('text')) return FileText;
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Format date using the global utility
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return formatDateTimeUtil(dateStr, getBrowserTimezone());
};

// Check if file is an image
const isImage = (mimeType) => mimeType?.startsWith('image/');

// Inline editable file row component
const EditableFileRow = ({ 
  file, 
  onDelete, 
  deletingId, 
  canDelete, 
  onUpdate 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: file.title || '',
    description: file.description || '',
    tags: file.tags || []
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const titleInputRef = useRef(null);

  const FileIcon = getFileIcon(file.mime_type, file.original_filename);

  // Focus title input when entering edit mode
  useEffect(() => {
    if (isEditing && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isEditing]);

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setEditData({
      title: file.title || '',
      description: file.description || '',
      tags: file.tags || []
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setTagInput('');
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/files/${file.file_id}`, {
        title: editData.title || null,
        description: editData.description || null,
        tags: editData.tags
      });
      
      // Update local file data
      onUpdate?.(file.file_id, editData);
      setIsEditing(false);
      toast.success('File updated');
    } catch (error) {
      console.error('Failed to update file:', error);
      toast.error('Failed to update file');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    const tag = tagInput.trim().toLowerCase();
    if (tag && !editData.tags.includes(tag)) {
      setEditData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    }
    if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const openFile = () => {
    if (file.url) {
      window.open(file.url, '_blank', 'noopener,noreferrer');
    }
  };

  if (isEditing) {
    return (
      <div 
        className="p-4 rounded-lg border bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header row with file info */}
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            {isImage(file.mime_type) && file.url ? (
              <img 
                src={`${file.url}?thumb=true`} 
                alt={file.original_filename}
                className="w-10 h-10 rounded object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <FileIcon className="w-5 h-5 text-gray-500" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-gray-700 dark:text-gray-200">
              {file.original_filename}
            </p>
            <p className="text-xs text-gray-500">
              {formatFileSize(file.size_bytes)} • {formatDate(file.uploaded_at)}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelEdit}
              disabled={saving}
              className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSaveEdit}
              disabled={saving}
              className="h-8 px-3 bg-blue-600 hover:bg-blue-700"
              title="Save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Title</label>
          <Input
            ref={titleInputRef}
            value={editData.title}
            onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
            onKeyDown={handleKeyDown}
            placeholder="Add a title..."
            className="h-9 text-sm"
            maxLength={200}
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Description</label>
          <Textarea
            value={editData.description}
            onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Add a description..."
            className="text-sm min-h-[60px] resize-none"
            maxLength={1000}
            rows={2}
          />
        </div>

        {/* Tags */}
        <div>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Tags</label>
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {editData.tags.map((tag, idx) => (
              <Badge 
                key={idx} 
                variant="secondary"
                className="text-xs px-2 py-0.5 flex items-center gap-1 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30"
                onClick={() => handleRemoveTag(tag)}
              >
                {tag}
                <X className="w-3 h-3" />
              </Badge>
            ))}
            {editData.tags.length === 0 && (
              <span className="text-xs text-gray-400">No tags</span>
            )}
          </div>
          <form onSubmit={handleAddTag} className="flex gap-2">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Add tag..."
              className="h-8 text-xs flex-1"
            />
            <Button 
              type="submit" 
              variant="outline" 
              size="sm" 
              className="h-8 px-2"
              disabled={!tagInput.trim()}
            >
              <Tag className="w-3 h-3" />
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Non-editing view
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 group cursor-pointer"
      onClick={openFile}
    >
      {/* Icon or Thumbnail (50% larger: 40px -> 60px) */}
      <div className="flex-shrink-0">
        {isImage(file.mime_type) && file.url ? (
          <img 
            src={`${file.url}?thumb=true`} 
            alt={file.original_filename}
            className="w-[60px] h-[60px] rounded object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-[60px] h-[60px] rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <FileIcon className="w-7 h-7 text-gray-500" />
          </div>
        )}
      </div>

      {/* File Info: Title + Date + Tags */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate text-gray-900 dark:text-gray-100" title={file.title || file.original_filename}>
          {file.title || file.original_filename}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {formatDate(file.uploaded_at)}
        </p>
        {file.tags && file.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {file.tags.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Actions - visible on hover only */}
      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleStartEdit(e);
          }}
          title="Edit metadata"
          className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600"
        >
          <Pencil className="w-4 h-4" />
        </Button>
        
        {canDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(file.file_id, file.original_filename);
            }}
            disabled={deletingId === file.file_id}
            title="Delete file"
            className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            {deletingId === file.file_id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

const FileList = ({
  entityType,
  entityId,
  canDelete = true,
  onFileDeleted,
  onCountChange,
  variant = 'list', // 'list' | 'grid' | 'compact'
  className
}) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { onCountChange?.(files.length); }, [files.length, onCountChange]);

  const fetchFiles = useCallback(async () => {
    if (!entityType || !entityId) {
      setFiles([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/api/files', {
        params: { entity_type: entityType, entity_id: entityId }
      });
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      // Don't show error toast for 403 - user might not have access yet
      if (error.response?.status !== 403) {
        toast.error('Failed to load files');
      }
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleDelete = async (fileId, filename) => {
    if (!window.confirm(`Delete "${filename}"? This cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(fileId);
      await api.delete(`/api/files/${fileId}`);
      setFiles(prev => prev.filter(f => f.file_id !== fileId));
      onFileDeleted?.(fileId);
      toast.success('File deleted');
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error('Failed to delete file');
    } finally {
      setDeletingId(null);
    }
  };

  const handleFileUpdate = (fileId, updates) => {
    setFiles(prev => prev.map(f => 
      f.file_id === fileId 
        ? { ...f, ...updates }
        : f
    ));
  };

  const openFile = (file) => {
    if (file.url) {
      window.open(file.url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-4", className)}>
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading files...</span>
      </div>
    );
  }

  if (files.length === 0) {
    return null; // Don't show anything if no files
  }

  // Compact variant - inline pills
  if (variant === 'compact') {
    return (
      <div className={cn("flex flex-wrap gap-2", className)}>
        {files.map((file) => {
          const FileIcon = getFileIcon(file.mime_type, file.original_filename);
          
          return (
            <div
              key={file.file_id}
              className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer group"
              onClick={() => openFile(file)}
              title={`${file.title || file.original_filename} (${formatFileSize(file.size_bytes)})`}
            >
              <FileIcon className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs text-gray-700 dark:text-gray-300 max-w-[120px] truncate">
                {file.title || file.original_filename}
              </span>
              {canDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(file.file_id, file.original_filename);
                  }}
                  className="opacity-0 group-hover:opacity-100 ml-1 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Grid variant - card layout with thumbnails
  if (variant === 'grid') {
    return (
      <div className={cn("grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3", className)}>
        {files.map((file) => {
          const FileIcon = getFileIcon(file.mime_type, file.original_filename);
          const isImg = isImage(file.mime_type) && file.url;
          
          return (
            <div
              key={file.file_id}
              className="relative group border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openFile(file)}
            >
              {/* Thumbnail or Icon */}
              <div className="aspect-square bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                {isImg ? (
                  <img 
                    src={`${file.url}?thumb=true`} 
                    alt={file.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <FileIcon className="w-12 h-12 text-gray-400" />
                )}
              </div>
              
              {/* Info */}
              <div className="p-2">
                <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100" title={file.title || file.original_filename}>
                  {file.title || file.original_filename}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(file.size_bytes)}
                </p>
              </div>

              {/* Delete button */}
              {canDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(file.file_id, file.original_filename);
                  }}
                  disabled={deletingId === file.file_id}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 h-7 w-7 p-0"
                >
                  {deletingId === file.file_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // List variant (default) - detailed rows with inline editing (2 columns)
  return (
    <div className={cn("", className)}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Attachments ({files.length})
        </h4>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {files.map((file) => (
          <EditableFileRow
            key={file.file_id}
            file={file}
            onDelete={handleDelete}
            deletingId={deletingId}
            canDelete={canDelete}
            onUpdate={handleFileUpdate}
          />
        ))}
      </div>
    </div>
  );
};

export default FileList;
